export const ROLES = {
  SYSTEM: "system",
  USER: "user",
  ASSISTANT: "assistant",
  FUNCTION: "function",
  TOOL: "tool",
  PLACEHOLDER: "placeholder",
  DEVELOPER: "developer",
} as const;

export const roles = [
  ROLES.SYSTEM,
  ROLES.USER,
  ROLES.ASSISTANT,
  ROLES.FUNCTION,
  ROLES.TOOL,
  ROLES.PLACEHOLDER,
  ROLES.DEVELOPER,
] as const;

//TODO replace useage of Role with RoleI
export type Role = (typeof roles)[number];
export type RoleI = Role;

export interface WebAnnotation {
  type: "url_citation";
  url: string;
  title: string;
  start_index: number;
  end_index: number;
  cited_text?: string;
}

export interface FileAnnotation {
  type: "file_citation";
  filename: string;
  file_id: string;
  index: number;
}

export interface MapAnnotation {
  type: "map_citation";
  url: string;
  title: string;
  place_id?: string;
  start_index: number;
  end_index: number;
  cited_text?: string;
}

export interface ContainerFileAnnotation {
  type: "container_file_citation";
  container_id: string;
  start_index?: number;
  end_index?: number;
  filename?: string;
  file_id?: string;
}

export interface CacheControl {
  type: "ephemeral";
  ttl?: "5m" | "1h";
}

export interface TextContent {
  type: "text";
  text: string;
  id?: string;
  annotations?: (WebAnnotation | FileAnnotation | MapAnnotation | ContainerFileAnnotation)[];
  thought_signature?: string;
  cache_control?: CacheControl;
}

export interface ThinkingContent {
  type: "thinking";
  thinking: string;
  signature?: string;
  id?: string;
}

export interface ImageURL {
  url: string;
  detail?: string;
}

export interface ImageContent {
  type: "image_url";
  image_url: ImageURL;
  image_variable?: string;
}

export const MEDIA_TYPES = {
  IMAGE: "image" as const,
  AUDIO: "audio" as const,
  VIDEO: "video" as const,
  APPLICATION_PDF: "application/pdf" as const,
} as const;

export type MediaType = (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES];

export interface Media {
  type: MediaType;
  url: string;
  title: string;
}

export interface MediaContent {
  type: "media";
  media: Media;
}

export interface MediaVariable {
  type: "media_variable";
  name: string;
}

/** Provider-specific fields for image generation output.
 * OpenAI: revised_prompt, background, size, quality, output_format
 * Gemini: aspect_ratio, image_size
 * OpenRouter TTS (speech): voice */
export interface OutputMediaProviderMetadata {
  revised_prompt?: string;
  background?: string;
  size?: string;
  quality?: string;
  output_format?: string;
  aspect_ratio?: string;
  image_size?: string;
  voice?: string;
  source?: "chat" | "speech";
}

export interface OutputMediaContent {
  type: "output_media";
  id?: string;
  url: string;
  mime_type?: string; // e.g. "image/png", "image/jpeg", "image/webp", "video/mp4", "audio/mp3"
  media_type?: "image" | "video" | "audio"; // For future extensibility
  provider_metadata?: OutputMediaProviderMetadata;
}

export interface CodeContent {
  type: "code";
  code: string;
  language?: string;
  id?: string;
  container_id?: string;
}

export interface CodeExecutionResultContent {
  type: "code_execution_result";
  output: string;
  outcome: string;
}

export interface McpTool {
  name: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input_schema?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  annotations?: Record<string, any>;
}

export interface McpListToolsContent {
  type: "mcp_list_tools";
  id?: string;
  server_label: string;
  tools: McpTool[];
  error?: string | Record<string, unknown> | null;
}

export interface McpCallContent {
  type: "mcp_call";
  id?: string;
  name: string;
  server_label: string;
  arguments: string;
  output?: string | null;
  error?: string | Record<string, unknown> | null;
  approval_request_id?: string | null;
}

export interface McpApprovalRequestContent {
  type: "mcp_approval_request";
  id?: string;
  name: string;
  arguments: string;
  server_label: string;
}

export interface McpApprovalResponseContent {
  type: "mcp_approval_response";
  approval_request_id: string;
  approve: boolean;
}

export interface ServerToolUseContent {
  type: "server_tool_use";
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface BashCodeExecutionToolResultContent {
  type: "bash_code_execution_tool_result";
  tool_use_id: string;
  content: {
    type: string;
    stdout?: string;
    stderr?: string;
    return_code?: number;
    error_code?: string;
    error_message?: string;
    content?: unknown[];
  };
}

export interface TextEditorCodeExecutionToolResultContent {
  type: "text_editor_code_execution_tool_result";
  tool_use_id: string;
  content: {
    type: string;
    // Error fields
    error_code?: string;
    error_message?: string;
    // Create result
    is_file_update?: boolean;
    // View result
    content?: string;
    file_type?: string;
    num_lines?: number;
    start_line?: number;
    total_lines?: number;
    // Str_replace result
    old_start?: number;
    old_lines?: number;
    new_start?: number;
    new_lines?: number;
    lines?: string[];
  };
}

export interface WebSearchResultItem {
  type: "web_search_result";
  url: string;
  title: string;
  encrypted_content: string;
  page_age?: string;
}

export interface WebSearchToolResultContent {
  type: "web_search_tool_result";
  tool_use_id: string;
  content: WebSearchResultItem[];
}

export interface ShellCallAction {
  commands: string[];
  max_output_length?: number;
  timeout_ms?: number;
}

export interface ShellCallOutputEntry {
  stdout: string;
  stderr: string;
  outcome: {
    type: "exit" | "timeout";
    exit_code?: number;
  };
}

export interface ShellCallContent {
  type: "shell_call";
  id?: string;
  call_id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: ShellCallAction | Record<string, any>;
  status?: string;
}

export interface ShellCallOutputContent {
  type: "shell_call_output";
  id?: string;
  call_id?: string;
  output: ShellCallOutputEntry[];
  status?: string;
}

export interface ApplyPatchOperation {
  type: "create_file" | "update_file" | "delete_file";
  path: string;
  diff?: string;
}

export interface ApplyPatchCallContent {
  type: "apply_patch_call";
  id?: string;
  call_id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  operation: ApplyPatchOperation | Record<string, any>;
  status?: string;
}

export interface ApplyPatchCallOutputContent {
  type: "apply_patch_call_output";
  id?: string;
  call_id?: string;
  output?: string;
  status?: string;
}

export const CONTENT_TYPES = {
  TEXT: "text",
  THINKING: "thinking",
  IMAGE_URL: "image_url",
  MEDIA: "media",
  MEDIA_VARIABLE: "media_variable",
  CODE: "code",
  OUTPUT_MEDIA: "output_media",
  CODE_EXECUTION_RESULT: "code_execution_result",
  MCP_LIST_TOOLS: "mcp_list_tools",
  MCP_CALL: "mcp_call",
  MCP_APPROVAL_REQUEST: "mcp_approval_request",
  MCP_APPROVAL_RESPONSE: "mcp_approval_response",
  WEB_SEARCH_TOOL_RESULT: "web_search_tool_result",
  SHELL_CALL: "shell_call",
  SHELL_CALL_OUTPUT: "shell_call_output",
  APPLY_PATCH_CALL: "apply_patch_call",
  APPLY_PATCH_CALL_OUTPUT: "apply_patch_call_output",
  SERVER_TOOL_USE: "server_tool_use",
  BASH_CODE_EXECUTION_TOOL_RESULT: "bash_code_execution_tool_result",
  TEXT_EDITOR_CODE_EXECUTION_TOOL_RESULT: "text_editor_code_execution_tool_result",
} as const;

export type ContentType = (typeof CONTENT_TYPES)[keyof typeof CONTENT_TYPES];

export type Content =
  | TextContent
  | ThinkingContent
  | ImageContent
  | MediaContent
  | MediaVariable
  | CodeContent
  | OutputMediaContent
  | CodeExecutionResultContent
  | McpListToolsContent
  | McpCallContent
  | McpApprovalRequestContent
  | McpApprovalResponseContent
  | WebSearchToolResultContent
  | ShellCallContent
  | ShellCallOutputContent
  | ApplyPatchCallContent
  | ApplyPatchCallOutputContent
  | ServerToolUseContent
  | BashCodeExecutionToolResultContent
  | TextEditorCodeExecutionToolResultContent;

export interface ContentTypeMap {
  [CONTENT_TYPES.TEXT]: TextContent;
  [CONTENT_TYPES.THINKING]: ThinkingContent;
  [CONTENT_TYPES.IMAGE_URL]: ImageContent;
  [CONTENT_TYPES.MEDIA]: MediaContent;
  [CONTENT_TYPES.MEDIA_VARIABLE]: MediaVariable;
  [CONTENT_TYPES.CODE]: CodeContent;
  [CONTENT_TYPES.OUTPUT_MEDIA]: OutputMediaContent;
  [CONTENT_TYPES.CODE_EXECUTION_RESULT]: CodeExecutionResultContent;
  [CONTENT_TYPES.MCP_LIST_TOOLS]: McpListToolsContent;
  [CONTENT_TYPES.MCP_CALL]: McpCallContent;
  [CONTENT_TYPES.MCP_APPROVAL_REQUEST]: McpApprovalRequestContent;
  [CONTENT_TYPES.MCP_APPROVAL_RESPONSE]: McpApprovalResponseContent;
  [CONTENT_TYPES.WEB_SEARCH_TOOL_RESULT]: WebSearchToolResultContent;
  [CONTENT_TYPES.SHELL_CALL]: ShellCallContent;
  [CONTENT_TYPES.SHELL_CALL_OUTPUT]: ShellCallOutputContent;
  [CONTENT_TYPES.APPLY_PATCH_CALL]: ApplyPatchCallContent;
  [CONTENT_TYPES.APPLY_PATCH_CALL_OUTPUT]: ApplyPatchCallOutputContent;
  [CONTENT_TYPES.SERVER_TOOL_USE]: ServerToolUseContent;
  [CONTENT_TYPES.BASH_CODE_EXECUTION_TOOL_RESULT]: BashCodeExecutionToolResultContent;
  [CONTENT_TYPES.TEXT_EDITOR_CODE_EXECUTION_TOOL_RESULT]: TextEditorCodeExecutionToolResultContent;
}

export const templateFormats = ["f-string", "jinja2"] as const;

export type TemplateFormat = (typeof templateFormats)[number];

export interface FunctionCall {
  name: string;
  arguments: string;
}

type ChatType = "chat";
type CompletionType = "completion";

export interface CompletionTemplate {
  type: CompletionType;
  content: Content[];
  input_variables: string[];
  template_format: "f-string" | "jinja2";
}

export interface JSONSchema {
  id?: string;
  $schema?: string;
  name?: string;
  title?: string;
  description?: string;
  multipleOf?: number;
  maximum?: number;
  exclusiveMaximum?: boolean;
  minimum?: number;
  exclusiveMinimum?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  additionalItems?: boolean | JSONSchema;
  items?: JSONSchema | JSONSchema[];
  maxItems?: number;
  minItems?: number;
  uniqueItems?: boolean;
  maxProperties?: number;
  minProperties?: number;
  required?: string[];
  additionalProperties?: boolean;
  definitions?: Record<string, JSONSchema>;
  properties?: Record<string, JSONSchema>;
  patternProperties?: Record<string, JSONSchema>;
  dependencies?: Record<string, JSONSchema | string[]>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  enum?: any[];
  type?: string | string[];
  format?: string;
  allOf?: JSONSchema[];
  anyOf?: JSONSchema[];
  oneOf?: JSONSchema[];
  not?: JSONSchema;
}

export interface CustomFunction_ {
  name: string;
  description: string;
  strict?: boolean;
  parameters?: JSONSchema;
  input_schema?: JSONSchema; // Support Anthropic format
  cache_control?: CacheControl;
}

// OpenAI File Search Tool Types
export interface ComparisonFilter {
  key: string;
  value: string | number | boolean;
  operation: "eq" | "ne" | "gt" | "gte" | "lt" | "lte";
}

export interface CompoundFilter {
  operator: "and" | "or";
  operands: (ComparisonFilter | CompoundFilter)[];
}

export type Filters = ComparisonFilter | CompoundFilter;

export interface RankingOptions {
  ranker?: "auto" | "default-2024-11-15";
  score_threshold?: number;
}

export interface FileSearchToolParam {
  type: "file_search";
  vector_store_ids: string[];
  filters?: Filters;
  max_num_results?: number;
  ranking_options?: RankingOptions;
}

// Web Search built-in tool: base + provider-specific config types.
export interface WebSearchToolFilters {
  allowed_domains?: string[];
}

export interface WebSearchToolUserLocation {
  city?: string;
  country?: string;
  region?: string;
  timezone?: string;
  type: "approximate";
}

/** OpenAI web search tool config. */
export interface OpenAIWebSearchToolParam {
  type: "web_search" | "web_search_2025_08_26";
  filters?: WebSearchToolFilters;
  search_context_size?: "low" | "medium" | "high";
  user_location?: WebSearchToolUserLocation;
}

/** Google / Vertex web search (grounding) tool config. No type field; backend dispatches by google_search key. */
export interface GoogleWebSearchToolParam {
  google_search: object;
}

/** Google / Vertex Maps grounding tool config. Backend dispatches by google_maps key. */
export interface GoogleMapsToolParam {
  google_maps: object;
}

/** OpenRouter web search config (maps to ``openrouter:web_search`` server tool). */
export interface OpenRouterWebSearchToolParam {
  id: "web";
  /** Search backend. Defaults to native when supported, else Exa. */
  engine?: "native" | "exa" | "firecrawl" | "parallel" | "perplexity";
  /** Max web results to attach (defaults to 5 on OpenRouter). */
  max_results?: number;
  /** Custom prompt used to attach results to the message stream (legacy plugin only). */
  search_prompt?: string;
  /** Restrict results to these domains (maps to the server tool's allowed_domains). */
  include_domains?: string[];
  /** Exclude results from these domains (maps to the server tool's excluded_domains). */
  exclude_domains?: string[];
}

/** Generic OpenRouter server tool config (``openrouter:<name>`` wire shape). */
export interface OpenRouterServerToolParam {
  /** Wire name after the ``openrouter:`` prefix (e.g. "datetime", "fusion"). */
  openrouter_server_tool: string;
  /** Optional per-tool options passed through to OpenRouter untouched. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameters?: Record<string, any>;
}

/** Google / Vertex code execution tool config. Backend dispatches by code_execution key. */
export interface GoogleCodeExecutionToolParam {
  code_execution: object;
}

/** Google / Vertex URL context tool config. Backend dispatches by url_context key. */
export interface GoogleUrlContextToolParam {
  url_context: object;
}

/** Google / Vertex file search tool config. Backend dispatches by file_search key (no type field). */
export interface GoogleFileSearchToolParam {
  file_search: {
    file_search_store_names?: string[];
  };
}

/** Anthropic bash tool config. */
export interface AnthropicBashToolParam {
  type: "bash_20250124";
  name: "bash";
}

/** Anthropic web search tool config. */
export interface AnthropicWebSearchToolParam {
  type: "web_search_20250305";
  name: "web_search";
  max_uses?: number;
  allowed_domains?: string[];
  blocked_domains?: string[];
  user_location?: WebSearchToolUserLocation;
}

/** Anthropic code execution tool config. */
export interface AnthropicCodeExecutionToolParam {
  type: "code_execution_20250522" | "code_execution_20250825" | "code_execution_20260120";
  name: "code_execution";
}

/** Anthropic text editor tool config. */
export interface AnthropicTextEditorToolParam {
  type: "text_editor_20250124" | "text_editor_20250429" | "text_editor_20250728";
  name: "str_replace_editor" | "str_replace_based_edit_tool";
}

/** Union of all web search configs; use the specific type per provider where possible. */
export type WebSearchToolParam =
  | OpenAIWebSearchToolParam
  | GoogleWebSearchToolParam
  | AnthropicWebSearchToolParam
  | GoogleMapsToolParam;

export interface CodeInterpreterToolParam {
  type: "code_interpreter";
  container: { type: "auto" };
}

export interface ImageGenerationToolParam {
  type: "image_generation";
}

export interface McpToolApprovalFilter {
  tool_names: string[];
}

export interface McpToolApprovalObject {
  never?: McpToolApprovalFilter;
  always?: McpToolApprovalFilter;
}

export interface McpSecretReference {
  env_var: string;
}

export interface McpToolParam {
  type: "mcp";
  server_url: string;
  server_label?: string;
  headers?: Record<string, McpSecretReference>;
  allowed_tools?: string[];
  require_approval?: "never";
}

export interface OpenAINativeMcpToolParam {
  type: "mcp";
  server_label: string;
  server_url?: string;
  server_description?: string;
  connector_id?: string;
  authorization?: string;
  headers?: Record<string, string>;
  allowed_tools?: string[];
  require_approval?: "always" | "never" | McpToolApprovalObject;
}

export interface ShellToolParam {
  type: "shell";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  environment?: Record<string, any>;
}

export interface ApplyPatchToolParam {
  type: "apply_patch";
}

export type BuiltInToolParam =
  | FileSearchToolParam
  | WebSearchToolParam
  | AnthropicBashToolParam
  | AnthropicTextEditorToolParam
  | GoogleMapsToolParam
  | GoogleCodeExecutionToolParam
  | GoogleUrlContextToolParam
  | GoogleFileSearchToolParam
  | AnthropicCodeExecutionToolParam
  | CodeInterpreterToolParam
  | ImageGenerationToolParam
  | OpenAINativeMcpToolParam
  | ShellToolParam
  | ApplyPatchToolParam
  | OpenRouterWebSearchToolParam
  | OpenRouterServerToolParam
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { type: string; [key: string]: any }; // Generic fallback for other providers

export interface BuiltInToolMetadata {
  name: string;
  description: string;
  provider: string;
  tool_type: string;
}

export interface FunctionTool {
  type: "function";
  function: Function_;
}

export interface ToolVariable {
  type: "variable";
  name: string;
}

export interface RegistryTool {
  type: "registry";
  tool_registry_id: number;
  label?: string | null;
  version_number?: number | null;
  _resolved_name?: string;
  /** Hydrated from the resolved tool version. True when the version has an execution config. */
  _has_execution?: boolean;
}

export interface PromptLayerMcpTool {
  type: "mcp";
  mcp_server_id: number;
  _resolved_name?: string;
  _is_available?: boolean;
}

export type Tool = FunctionTool | PromptLayerMcpTool | BuiltInTool | ToolVariable | RegistryTool;

export type BuiltInToolProvider =
  | "openai"
  | "openai.azure"
  | "google"
  | "vertexai"
  | "anthropic"
  | "openrouter";

export type BuiltInToolType =
  | "web_search"
  | "file_search"
  | "code_interpreter"
  | "image_generation"
  | "google_maps"
  | "url_context"
  | "openai_mcp"
  | "bash"
  | "shell"
  | "apply_patch"
  | "text_editor"
  // OpenRouter server tools (https://openrouter.ai/docs/guides/features/server-tools).
  | "datetime"
  | "web_fetch"
  | "fusion"
  | "advisor"
  | "subagent"
  | "search_models";

export interface BuiltInTool {
  id: string;
  name: string;
  description: string;
  provider: string;
  type: BuiltInToolType;
  config: BuiltInToolParam;
}

export type Function_ = CustomFunction_ | PromptLayerMcpTool | BuiltInTool;

export interface MessageFunctionCall {
  name: string;
}

export type ChatFunctionCall = string | MessageFunctionCall;

export interface BaseMessage {
  content?: Content[];
  input_variables?: string[];
  template_format: TemplateFormat;
  raw_request_display_role?: string;
}

export interface SystemMessage extends BaseMessage {
  role: (typeof roles)[0];
  content: Content[];
  name?: string;
}

export interface UserMessage extends BaseMessage {
  role: (typeof roles)[1];
  content: Content[];
  name?: string;
}

export interface ToolCall {
  tool_id?: string;
  id: string;
  type: "function";
  function: FunctionCall;
  cache_control?: CacheControl;
  /** Provider-specific metadata (e.g. Gemini thought_signature). */
  provider_metadata?: Record<string, unknown> | null;
  _id?: string;
}

export interface AssistantMessage extends BaseMessage {
  role: (typeof roles)[2];
  function_call?: FunctionCall;
  name?: string;
  tool_calls?: ToolCall[];
}

export interface FunctionMessage extends BaseMessage {
  role: (typeof roles)[3];
  name: string;
}

export interface ToolMessage extends BaseMessage {
  role: (typeof roles)[4];
  content: Content[];
  tool_call_id: string;
  name?: string;
  cache_control?: CacheControl;
}

export interface PlaceholderMessage extends BaseMessage {
  role: (typeof roles)[5];
  name: string;
}

export interface DeveloperMessage extends BaseMessage {
  role: (typeof roles)[6];
  content: Content[];
  name?: string;
}

export interface ChatToolChoice {
  type: "function";
  function: MessageFunctionCall;
}

export type ToolChoice = string | ChatToolChoice;

export interface Tag {
  id: number;
  workspace_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export type Message =
  | SystemMessage
  | UserMessage
  | AssistantMessage
  | FunctionMessage
  | ToolMessage
  | PlaceholderMessage
  | DeveloperMessage;

export interface ChatTemplate {
  type: ChatType;
  messages: Message[];
  functions?: Function_[];
  function_call?: ChatFunctionCall;
  input_variables: string[];
  tools?: Tool[];
  tool_choice?: ToolChoice;
  json_schema?: {
    json_schema: JSONSchema;
    type: "json_schema";
  };
}

export interface Model {
  provider: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameters: Record<string, any>;
  model_config_display_name?: string | null;
  display_params?: Record<string, [value: string | boolean | null]>;
  model_config_id?: number | null;
  base_model?: string | null;
  api_type?: string | null;
}

export type DecisionQuestionType = "binary" | "choice" | "score";

export interface DecisionOption {
  value: string | boolean;
  description?: string | null;
}

/** Provider-neutral decision question, stored in `metadata.model.parameters.questions`. */
export interface DecisionQuestion {
  name: string;
  type: DecisionQuestionType;
  instructions: string;
  options?: DecisionOption[];
}

/** Provider-neutral decision answer, as returned in request details. */
export interface DecisionAnswer {
  name: string;
  type: DecisionQuestionType | "refusal";
  value: number | string | boolean | null;
  probabilities: Record<string, number>;
  confidence: number | null;
}

export interface Metadata {
  model?: Model;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export type Template = (ChatTemplate | CompletionTemplate) & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataset_examples?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  functions?: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tools?: Record<string, any>[] | null;
};

export interface BasePromptTemplate {
  prompt_name?: string;
  tags?: string[];
  folder_id?: number;
  workspace_id?: number;
  is_snippet?: boolean;
}

export interface PromptBlueprint {
  prompt_template: Template;
  inference_client_name?: string | null;
  commit_message?: string;
  metadata?: Metadata;
  report_id?: number;
  provider_base_url_name: string | null;
  provider_id?: number;
  input_variable_sets?: Record<string, unknown>[][];
  input_variable_set_names?: string[];
}

export type CreatePromptVersion = PromptBlueprint & {
  prompt_id: number;
  parent_version_id?: number | null;
} & { snippet_overrides?: Record<string, string> };

export interface CreatePromptTemplate {
  prompt_template: BasePromptTemplate;
  prompt_version: PromptBlueprint;
  snippet_overrides?: Record<string, string>;
}

export const functionsTypes = ["functions", "tools"] as const;

export type FunctionsType = (typeof functionsTypes)[number];

export interface SchemaDefinition {
  name?: string;
  description?: string;
  strict?: boolean;
  schema?: JSONSchema;
}

export type InferenceClientRequestBody =
  | {
      api_key: string;
    }
  | InferenceClient;

export interface InferenceClient {
  id: number;
  cookies: Record<string, string> | null;
  headers: Record<string, string> | null;
  name: string;
  model: string;
  timeout: number | null;
  proxies: string[] | null;
  workspace_id: number;
}
